import { ConfirmationResult, getAuth, RecaptchaVerifier } from 'firebase/auth';
import { ensureMsg91 } from "../utils/ensureMsg91";
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { app } from '../firebaseClient';
import { supabase } from '../supabaseClient';
import { trackEvent } from '../utils/analytics';
import { useAuth } from '../context/AuthContext';
import { useTwilioOtp } from '../hooks/useTwilioOtp';
import { countries, Country } from '../data/countries';
import './AuthModal.css';
import { peekAuthSource } from '../utils/authSource';
import { spreadOtpDigits, otpFocusIndex, overtypedDigit } from '../utils/otp';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  onGoogleSignIn: () => void;
  phoneAuth?: {
    requestOtp: (payload: { phone: string }) => Promise<void>;
    verifyOtp: (payload: { phone: string; otp: string }) => Promise<void>;
  };
  firebasePhoneAuth?: {
    requestOtp: (payload: { phone: string; recaptchaVerifier: RecaptchaVerifier }) => Promise<ConfirmationResult>;
    verifyOtp: (payload: { confirmationResult: ConfirmationResult; otp: string }) => Promise<void>;
  };
  msg91Auth?: {
    verifyOtp: (payload: { accessToken: string; phone: string; whatsappMarketingOptIn?: boolean; countryCode?: string }) => Promise<void>;
  };
}

// Replaced with data/countries.ts

// Pull the JSON `error` field out of a Supabase Functions non-2xx response.
async function extractFnError(error: any, fallback: string): Promise<string> {
  try {
    const body = await error?.context?.json?.();
    if (body?.error) return body.error;
  } catch { /* ignore */ }
  return error?.message || fallback;
}

const AuthModal: React.FC<AuthModalProps> = ({ isOpen, onClose, onGoogleSignIn, phoneAuth, firebasePhoneAuth, msg91Auth }) => {
  const isGlobalPage = window.location.pathname === '/global';
  const [step, setStep] = useState<'phone' | 'otp'>('phone');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [countryCode, setCountryCode] = useState('+91');
  const [selectedIsoCode, setSelectedIsoCode] = useState('IN'); // [NEW] Track ISO for correct flag matching
  const [isCountryDropdownOpen, setIsCountryDropdownOpen] = useState(false);
  const [countrySearch, setCountrySearch] = useState('');
  // ≤640px renders the country picker as a portal bottom sheet instead of the
  // in-modal dropdown (which the mobile bottom-sheet modal clips invisible).
  const [isMobileViewport, setIsMobileViewport] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 640px)').matches,
  );
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 640px)');
    const onChange = (e: MediaQueryListEvent) => setIsMobileViewport(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  // OTP length mirrors the app (otp_config.dart): 4-digit codes via MSG91,
  // except numbers pinned to 6 — the App-Review test account (7093727560) gets
  // no real SMS; its only valid code is the fixed 6-digit REVIEW_TEST_OTP,
  // which a 4-box field could never accept. Twilio Verify is service-configured
  // at 6, so that route stays 6 too. The server echoes `otp_length` back and
  // that echo always wins, so UI and SMS can never disagree.
  const ALWAYS_SIX_DIGIT_PHONES = ['7093727560'];
  const otpDigitsForPhone = (phone: string): number => {
    const digits = (phone || '').replace(/\D/g, '');
    if (ALWAYS_SIX_DIGIT_PHONES.some(t => digits.endsWith(t))) return 6;
    return 4;
  };
  const [otpLength, setOtpLength] = useState(6);
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const applyOtpLength = (len: number) => {
    const clamped = Number.isInteger(len) && len >= 4 && len <= 8 ? len : 6;
    setOtpLength(clamped);
    setOtp(Array(clamped).fill(''));
  };
  // Track which OTP provider was used for sending (to route verify correctly)
  const [otpProvider, setOtpProvider] = useState<'msg91' | 'twilio'>('msg91');
  // Bumped on every send and resend, so the SMS listener and the focus effect
  // below re-arm for the new code even when the step itself did not change.
  const [otpRound, setOtpRound] = useState(0);
  // The verify routine is declared after the `!isOpen` early return, so the
  // effects (which must sit above it) reach it through this ref.
  const verifyCodeRef = useRef<((code: string) => void) | null>(null);
  // The last code auto-submitted, so a wrong code is not re-sent on every
  // render; an edit, a resend or the Verify button is what tries again.
  const lastAutoCodeRef = useRef<string | null>(null);
  const verifyInFlightRef = useRef(false);

  // Auto-tick WhatsApp
  const [agreeWhatsApp, setAgreeWhatsApp] = useState(true);

  // [NEW] Track Master Toggle
  const [isTwilioEnabled, setIsTwilioEnabled] = useState(false);

  // [NEW] Fetch Master Toggle
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const { data, error } = await supabase
          .from('system_settings')
          .select('is_active')
          .eq('setting_name', 'enable_twilio_otp')
          .single();

        if (error) {
          console.error('[AuthModal] Failed to fetch Twilio settings:', error);
          // Default remains false (Safe Mode)
        }

        if (data) {
          setIsTwilioEnabled(data.is_active);
          console.log('[AuthModal] Twilio Toggle Status:', data.is_active);
        }
      } catch (err) {
        console.error('[AuthModal] Unexpected error fetching settings:', err);
      }
    };
    fetchSettings();
  }, []);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Resend OTP Timer State
  const [resendTimer, setResendTimer] = useState(0);

  const dropdownRef = useRef<HTMLDivElement>(null);
  // The mobile country picker is a portal under document.body (the modal
  // container's overflow:hidden + transform clip/trap anything positioned
  // inside it — the "no search box, endless scrolling" bug). Portal content is
  // outside dropdownRef, so outside-click handling checks this ref too.
  const countrySheetRef = useRef<HTMLDivElement>(null);
  const recaptchaVerifierRef = useRef<RecaptchaVerifier | null>(null);
  const confirmationResultRef = useRef<ConfirmationResult | null>(null);
  const recaptchaContainerRef = useRef<HTMLDivElement>(null);
  const msg91CaptchaContainerRef = useRef<HTMLDivElement>(null);
  const msg91AccessTokenRef = useRef<string | null>(null);
  const msg91ReqIdRef = useRef<string | null>(null);

  // We track this but won't block the button with it
  const [msg91CaptchaVerified, setMsg91CaptchaVerified] = useState(false);

  const [msg91Loaded, setMsg91Loaded] = useState(false);
  const [msg91Initialized, setMsg91Initialized] = useState(false);
  const msg91InitializedRef = useRef(false);

  // [NEW] Hooks
  const { userCountry, pricingVariant } = useAuth();
  const twilioOtp = useTwilioOtp();

  // [NEW] Auto-set Country Code
  useEffect(() => {
    if (isOpen && userCountry) {
      const detectedCountry = countries.find(c => c.iso === userCountry.toUpperCase());
      if (detectedCountry) {
        setCountryCode(detectedCountry.code);
        setSelectedIsoCode(detectedCountry.iso); // [NEW] Also set ISO
        console.log(`[AuthModal] Autoselected country: ${detectedCountry.name} (${detectedCountry.iso}: ${detectedCountry.code})`);
      }
    }
  }, [isOpen, userCountry]);

  // Timer logic for Resend OTP
  useEffect(() => {
    let timerInterval: any;
    if (resendTimer > 0) {
      timerInterval = setInterval(() => {
        setResendTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(timerInterval);
  }, [resendTimer]);

  useEffect(() => {
    if (isOpen) {
      trackEvent('AuthModal Viewed', {
        method_available: msg91Auth ? 'msg91' : 'firebase'
      });
    }
  }, [isOpen, msg91Auth]);

  // Stable callbacks for MSG91 config
  const msg91SuccessCallback = useCallback((data: any) => {
    if (data?.message) {
      msg91AccessTokenRef.current = data.message;
    } else if (data?.access_token) {
      msg91AccessTokenRef.current = data.access_token;
    }
    if (data?.reqId) {
      msg91ReqIdRef.current = data.reqId;
    }
  }, []);

  const msg91FailureCallback = useCallback((error: any) => {
    console.error('[MSG91] Failure callback:', error);
    setError(error?.message || 'Failed to send OTP');
  }, []);

  // Reset MSG91 initialization state when modal closes
  useEffect(() => {
    if (!isOpen) {
      msg91InitializedRef.current = false;
      setMsg91Initialized(false);
      setMsg91Loaded(false);
      setMsg91CaptchaVerified(false);
      msg91AccessTokenRef.current = null;
      msg91ReqIdRef.current = null;
      setResendTimer(0);
    }
  }, [isOpen]);

  // Force MSG91 widget initialization when modal opens
  useEffect(() => {
    if (!isOpen || !msg91Auth || msg91InitializedRef.current) return;

    // The widget script is no longer injected at page load (CWV item 26);
    // opening this modal is the first and only trigger. The init loop below
    // polls msg91ScriptLoaded and surfaces its own timeout to the user, so a
    // rejection here needs no second error path.
    ensureMsg91().catch(() => {});

    const timeoutId = setTimeout(() => {
      const forceInit = async () => {
        let scriptAttempts = 0;
        // 40 x 300ms = 12s: otp-provider.js now injects after first paint
        // (worst case the 4s failsafe), so the old 6s budget could expire
        // on slow connections before the script ever arrived.
        while (scriptAttempts < 40 && !(window as any).msg91ScriptLoaded) {
          await new Promise(resolve => setTimeout(resolve, 300));
          scriptAttempts++;
        }

        let attempts = 0;
        while (attempts < 40) {
          const captchaContainer = document.getElementById('msg91-captcha-container');
          const hasInitFunction = typeof window.initSendOTP === 'function';

          if (captchaContainer && hasInitFunction) {
            if (!msg91InitializedRef.current) {
              try {
                const currentConfig = {
                  widgetId: '356b776c4e50333131353237',
                  tokenAuth: '479288Tnaem2li69358cbcP1',
                  exposeMethods: true,
                  captchaRenderId: 'msg91-captcha-container',
                  success: msg91SuccessCallback,
                  failure: msg91FailureCallback,
                };

                if (window.initSendOTP) {
                  window.initSendOTP(currentConfig);
                }
                msg91InitializedRef.current = true;
                setMsg91Loaded(true);
                setMsg91Initialized(true);

                // Wait a bit for background captcha
                await new Promise(resolve => setTimeout(resolve, 1500));
                break;
              } catch (err) {
                console.error('[MSG91] ❌ Initialization error:', err);
                break;
              }
            } else {
              break;
            }
          }
          await new Promise(resolve => setTimeout(resolve, 300));
          attempts++;
        }
      };
      forceInit();
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [isOpen, msg91Auth, msg91SuccessCallback, msg91FailureCallback]);

  // Poll for captcha verification status
  useEffect(() => {
    if (!msg91Auth || !msg91Initialized) return;

    const checkCaptcha = setInterval(() => {
      if (typeof window.isCaptchaVerified === 'function') {
        const isVerified = window.isCaptchaVerified();
        if (isVerified) {
          setMsg91CaptchaVerified(true);
        } else {
          setMsg91CaptchaVerified(false);
        }
      }
    }, 500);

    return () => clearInterval(checkCaptcha);
  }, [msg91Auth, msg91Initialized]);

  // Close dropdown when clicking outside. The mobile menu is a PORTAL bottom
  // sheet living under document.body (see countrySheetRef) — it is NOT inside
  // dropdownRef, so it must be checked separately or the first tap inside the
  // sheet (e.g. on the search box) would instantly close it.
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const t = event.target as Node;
      if (
        dropdownRef.current && !dropdownRef.current.contains(t) &&
        (!countrySheetRef.current || !countrySheetRef.current.contains(t))
      ) {
        setIsCountryDropdownOpen(false);
      }
    };
    if (isCountryDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isCountryDropdownOpen]);

  // Initialize reCAPTCHA for Firebase (Fallback)
  useEffect(() => {
    if (isOpen && !msg91Auth && firebasePhoneAuth && !recaptchaVerifierRef.current) {
      try {
        const auth = getAuth(app);
        recaptchaVerifierRef.current = new RecaptchaVerifier(auth, 'recaptcha-container', {
          size: 'invisible',
          callback: () => console.log('reCAPTCHA verified'),
          'expired-callback': () => {
            recaptchaVerifierRef.current = null;
          }
        });
      } catch (err) {
        console.error('Failed to initialize reCAPTCHA:', err);
      }
    }
    return () => {
      if (recaptchaVerifierRef.current) {
        try { recaptchaVerifierRef.current.clear(); } catch (err) { }
        recaptchaVerifierRef.current = null;
      }
      confirmationResultRef.current = null;
    };
  }, [isOpen, firebasePhoneAuth, msg91Auth]);

  // Prevent body scroll
  useEffect(() => {
    if (isOpen) {
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
    } else {
      const scrollY = document.body.style.top;
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
      if (scrollY) window.scrollTo(0, parseInt(scrollY || '0') * -1);
    }
    return () => {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Focus the first box as the OTP step opens, like the app's field. iOS only
  // offers the SMS code above the keyboard for a focused field.
  useEffect(() => {
    if (!isOpen || step !== 'otp') return;
    const t = window.setTimeout(() => document.getElementById('otp-0')?.focus(), 0);
    return () => window.clearTimeout(t);
  }, [isOpen, step, otpRound]);

  // Android Chrome: WebOTP reads the code from the SMS after one "Allow" tap,
  // the web's nearest thing to the app's sms_autofill. Chrome only hands over
  // an SMS whose LAST line is "@astroaura.ai #<code>"; any other SMS leaves the
  // request pending until the cleanup aborts it, so this is harmless before the
  // template carries that line. Absent on iOS, desktop and Firefox, which keep
  // autocomplete="one-time-code".
  useEffect(() => {
    if (!isOpen || step !== 'otp' || !('OTPCredential' in window)) return;
    const ac = new AbortController();
    navigator.credentials
      .get({ otp: { transport: ['sms'] }, signal: ac.signal } as any)
      .then((cred: any) => {
        if (ac.signal.aborted || !cred?.code) return;
        setOtp(prev => spreadOtpDigits(Array(prev.length).fill(''), 0, cred.code));
        trackEvent('OTP Autofilled', { method: 'webotp' });
      })
      .catch(() => { /* aborted, dismissed, or timed out */ });
    return () => ac.abort();
  }, [isOpen, step, otpRound]);

  // Verify as soon as every box holds a digit, typed, pasted or autofilled, as
  // the app does (otp_verify_screen.dart onCodeChanged). Each distinct code is
  // submitted once; Verify stays for a deliberate retry of the same digits.
  useEffect(() => {
    if (!isOpen || step !== 'otp' || isLoading) return;
    if (otp.length !== otpLength || otp.some(d => !d)) return;
    const code = otp.join('');
    if (code === lastAutoCodeRef.current) return;
    lastAutoCodeRef.current = code;
    // Drop the keyboard, as the app does, so the result is not hidden behind it.
    (document.activeElement as HTMLElement | null)?.blur?.();
    verifyCodeRef.current?.(code);
  }, [isOpen, step, otp, otpLength, isLoading]);

  if (!isOpen) return null;



  const handleSendOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!phoneNumber) return;

    setIsLoading(true);
    setError(null);
    msg91AccessTokenRef.current = null;
    msg91ReqIdRef.current = null;

    try {
      const fullPhoneNumber = countryCode + phoneNumber;
      const phoneWithoutPlus = fullPhoneNumber.startsWith('+') ? fullPhoneNumber.substring(1) : fullPhoneNumber;

      // [NEW] Hybrid Routing Logic
      // Route to MSG91 if: (1) Indian Number OR (2) Twilio is Disabled
      const useMsg91 = countryCode === '+91' || !isTwilioEnabled;

      if (!useMsg91) {
        // --- ROUTE B: TWILIO ---
        try {
          console.log('[AuthModal] Routing to Twilio');
          await twilioOtp.sendOtp(fullPhoneNumber);
          setOtpProvider('twilio');
          // Twilio Verify's code length is configured on the SERVICE (6), not
          // per-request — the 4-digit rollout is MSG91-only, like the app.
          applyOtpLength(6);
          trackEvent('OTP Sent', { method: 'twilio', country_code: countryCode });
        } catch (twilioErr: any) {
          // If Twilio fails (even if enabled), check if it was a disabled error or fallback
          if (twilioErr?.message?.includes('TWILIO_DISABLED') || twilioErr?.code === 'TWILIO_DISABLED') {
            console.log('[AuthModal] Twilio disabled (backend). Falling back to MSG91.');
            await sendMsg91OTP(phoneWithoutPlus);
            setOtpProvider('msg91');
            trackEvent('OTP Sent', { method: 'msg91-fallback', country_code: countryCode });
          } else {
            throw twilioErr;
          }
        }
      } else {
        // --- ROUTE A: MSG91 (Widget) ---
        console.log(`[AuthModal] Routing to MSG91 (Reason: ${countryCode === '+91' ? 'Indian' : 'Twilio Disabled'})`);
        await sendMsg91OTP(phoneWithoutPlus);
        setOtpProvider('msg91');
        trackEvent('OTP Sent', { method: 'msg91', country_code: countryCode });
      }

      setStep('otp');
      setResendTimer(30);
      lastAutoCodeRef.current = null;
      setOtpRound(r => r + 1);

    } catch (err: any) {
      setError(err?.message || 'Failed to send OTP. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * MSG91 OTP Sender — API-based via the `send-otp-v2` edge function, the same
   * server-side flow the mobile app uses (MSG91 OTP API `/api/v5/otp` with a
   * DLT template + authkey, on the FUNDED route). Bypasses the dead client-side
   * OTP widget entirely.
   */
  const sendMsg91OTP = async (phoneWithoutPlus: string) => {
    const requestedLength = otpDigitsForPhone(phoneWithoutPlus);
    const { data, error } = await supabase.functions.invoke('send-otp-v2', {
      body: { phone: phoneWithoutPlus, otp_length: requestedLength },
    });

    if (error) {
      throw new Error(await extractFnError(error, 'Failed to send OTP. Please try again.'));
    }
    if (!data?.success) {
      throw new Error(data?.error || 'Failed to send OTP. Please try again.');
    }

    msg91ReqIdRef.current = data.reqId || null;
    // The server's echo is authoritative (it forces 6 for the review-test
    // number and clamps unknown values) — render exactly what was SENT.
    applyOtpLength(Number(data.otp_length) || requestedLength);
    console.log('[AuthModal] OTP sent via send-otp-v2');
    return data;
  };

  const handleResendOTP = async () => {
    if (resendTimer > 0) return;

    setIsLoading(true);
    setError(null);

    try {
      if (otpProvider === 'twilio') {
        // Resend via Twilio
        const fullPhoneNumber = countryCode + phoneNumber;
        await twilioOtp.sendOtp(fullPhoneNumber);
        console.log('[Twilio] Resend Success');
      } else {
        // Resend via MSG91 (API) — MUST request the same otp_length as the
        // original send: omitting it makes the server default to 6, and the
        // user would be typing a 6-digit code into a 4-box field.
        const fullPhoneNumber = countryCode + phoneNumber;
        const { data, error } = await supabase.functions.invoke('send-otp-v2', {
          body: { phone: fullPhoneNumber, otp_length: otpDigitsForPhone(fullPhoneNumber) },
        });
        if (error || !data?.success) {
          throw new Error(error ? await extractFnError(error, 'Could not resend OTP. Please wait.') : (data?.error || 'Could not resend OTP. Please wait.'));
        }
        if (Number(data.otp_length)) applyOtpLength(Number(data.otp_length));
        console.log('[MSG91] Resend Success');
      }

      // Reset timer to 30 seconds
      setResendTimer(30);
      // The old code is dead. Clear it, or auto-verify would submit it again.
      setOtp(prev => Array(prev.length).fill(''));
      lastAutoCodeRef.current = null;
      setOtpRound(r => r + 1);
      trackEvent('OTP Resent', { method: countryCode === '+91' ? 'msg91' : 'twilio' });
    } catch (err: any) {
      // Show specific error (e.g., "Max limit reached")
      setError(err?.message || 'Could not resend OTP. Please wait.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    if (otp.some(digit => !digit)) return;
    await verifyCode(otp.join(''));
  };

  // Shared by the Verify button and auto-verify. The in-flight ref stops a
  // second submit landing before isLoading has re-rendered the button disabled.
  const verifyCode = async (otpValue: string) => {
    if (verifyInFlightRef.current) return;
    verifyInFlightRef.current = true;
    setIsLoading(true);
    setError(null);

    try {
      const fullPhoneNumber = countryCode + phoneNumber;

      if (otpProvider === 'twilio') {
        // --- ROUTE B: TWILIO ---
        await twilioOtp.verifyOtp({
          phone: fullPhoneNumber,
          otp: otpValue,
          whatsappMarketingOptIn: agreeWhatsApp,
          countryCode: userCountry
        });
        trackEvent('Login Success', { method: 'twilio' });
        onClose();
      } else {
        // --- ROUTE A: MSG91 (API-based via edge functions, same as the app) ---
        const { data, error: verifyError } = await supabase.functions.invoke('verify-otp-v2', {
          body: {
            phone: fullPhoneNumber,
            otp: otpValue,
            variant_name: pricingVariant || 'control',
            // Stamped onto the new user as their acquisition source. Both verify
            // paths need this — the modal offers the widget/token flow next to
            // this typed one, so sending it from only one loses about half the
            // signups the campaign paid for.
            source: peekAuthSource() || undefined,
          },
        });

        if (verifyError) {
          throw new Error(await extractFnError(verifyError, 'Invalid or expired OTP. Please try again.'));
        }
        if (!data?.success) {
          throw new Error(data?.error || 'Invalid or expired OTP. Please try again.');
        }

        // Exchange the returned magic-link token for a real Supabase session.
        let tokenHash: string | null = data.token_hash || null;
        if (!tokenHash && data.action_link) {
          try { tokenHash = new URL(data.action_link).searchParams.get('token_hash'); } catch { /* ignore */ }
        }
        if (!tokenHash) throw new Error('Could not establish your session. Please try again.');

        const { error: sessionError } = await supabase.auth.verifyOtp({
          token_hash: tokenHash,
          type: (data.type || 'magiclink') as any,
        });
        if (sessionError) throw sessionError;

        trackEvent('Login Success', { method: 'msg91' });
        onClose();
      }

    } catch (err: any) {
      setError('Invalid OTP. Please try again.');
      trackEvent('Login Failed', { error: err?.message });
    } finally {
      verifyInFlightRef.current = false;
      setIsLoading(false);
    }
  };
  verifyCodeRef.current = verifyCode;

  /**
   * Spread a multi-digit string across the boxes from `start`.
   *
   * Shared by paste and by change, because both can deliver more than one
   * digit at once: a paste obviously, and a change when Android/iOS SMS
   * autofill drops the whole code into the focused input. Handling only the
   * single-digit case is what made "paste the OTP" keep just the first digit.
   */
  const fillOtpFrom = (start: number, raw: string) => {
    const next = spreadOtpDigits(otp, start, raw);
    if (next === otp) return; // nothing numeric in there
    setOtp(next);
    document.getElementById(`otp-${otpFocusIndex(start, raw, otpLength)}`)?.focus();
  };

  const handleOtpChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return;

    // More than one digit arrived in a single box. Either the user typed over a
    // digit that was already there (one correction), or a code was delivered
    // whole by autofill / a paste that reached onChange rather than onPaste.
    let digit = value;
    if (value.length > 1) {
      const corrected = overtypedDigit(otp[index], value);
      if (corrected === null) {
        fillOtpFrom(index, value);
        return;
      }
      digit = corrected;
    }

    const newOtp = [...otp];
    newOtp[index] = digit;
    setOtp(newOtp);
    if (digit && index < otpLength - 1) {
      const nextInput = document.getElementById(`otp-${index + 1}`);
      nextInput?.focus();
    }
  };

  /**
   * Paste the whole code into any box and it spreads across the rest.
   *
   * An explicit handler rather than leaning on onChange: onChange gives no way
   * to tell a paste from typing, and the two need different handling once a box
   * can hold more than one character (see overtypedDigit). Tolerates the spaces
   * and dashes people copy out of an SMS ("123 456").
   */
  const handleOtpPaste = (index: number, e: React.ClipboardEvent) => {
    const text = e.clipboardData?.getData("text") || "";
    if (!/\d/.test(text)) return;
    e.preventDefault();
    fillOtpFrom(index, text);
  };

  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      const prevInput = document.getElementById(`otp-${index - 1}`);
      prevInput?.focus();
    }
  };

  return createPortal(
    <div className="auth-modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="auth-modal-container">
        {/* Left Panel - Features */}
        <div className="auth-features-panel">
          <div className="auth-features-content">
            <div className="auth-star-decoration">
              <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M24 0L27.5 16.5L44 20L27.5 23.5L24 40L20.5 23.5L4 20L20.5 16.5L24 0Z" fill="url(#star-gradient)" />
                <defs>
                  <linearGradient id="star-gradient" x1="4" y1="0" x2="44" y2="40" gradientUnits="userSpaceOnUse">
                    <stop stopColor="#ED0687" />
                    <stop offset="1" stopColor="#FE6C0F" />
                  </linearGradient>
                </defs>
              </svg>
            </div>

            <h2 className="auth-welcome-title">Welcome to Your Cosmic Journey</h2>
            <p className="auth-welcome-subtitle">
              Discover personalized astrology insights powered by AI
            </p>

            <div className="auth-feature-cards">
              <div className="auth-feature-card">
                <div className="auth-feature-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" stroke="url(#icon-gradient)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <defs>
                      <linearGradient id="icon-gradient" x1="2" y1="2" x2="22" y2="21" gradientUnits="userSpaceOnUse">
                        <stop stopColor="#ED0687" />
                        <stop offset="1" stopColor="#FE6C0F" />
                      </linearGradient>
                    </defs>
                  </svg>
                </div>
                <div className="auth-feature-text">
                  <h3>{isGlobalPage ? 'Safe' : 'AI-Powered Astrology'}</h3>
                  <p>{isGlobalPage ? 'A space where you can talk without judgment.' : 'Get instant, accurate predictions powered by advanced AI'}</p>
                </div>
              </div>

              <div className="auth-feature-card">
                <div className="auth-feature-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="3" y="11" width="18" height="11" rx="2" stroke="url(#icon-gradient-2)" strokeWidth="2" />
                    <path d="M7 11V7C7 4.79086 8.79086 3 11 3H13C15.2091 3 17 4.79086 17 7V11" stroke="url(#icon-gradient-2)" strokeWidth="2" strokeLinecap="round" />
                    <circle cx="12" cy="16" r="1" fill="url(#icon-gradient-2)" />
                    <defs>
                      <linearGradient id="icon-gradient-2" x1="3" y1="3" x2="21" y2="22" gradientUnits="userSpaceOnUse">
                        <stop stopColor="#ED0687" />
                        <stop offset="1" stopColor="#FE6C0F" />
                      </linearGradient>
                    </defs>
                  </svg>
                </div>
                <div className="auth-feature-text">
                  <h3>{isGlobalPage ? 'Private' : '100% Privacy'}</h3>
                  <p>{isGlobalPage ? 'Your conversations are never shared with anyone.' : 'Your data is secure and never shared with third parties'}</p>
                </div>
              </div>

              <div className="auth-feature-card">
                <div className="auth-feature-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="12" cy="12" r="10" stroke="url(#icon-gradient-3)" strokeWidth="2" />
                    <path d="M8 14C8 14 9.5 16 12 16C14.5 16 16 14 16 14" stroke="url(#icon-gradient-3)" strokeWidth="2" strokeLinecap="round" />
                    <circle cx="9" cy="9" r="1" fill="url(#icon-gradient-3)" />
                    <circle cx="15" cy="9" r="1" fill="url(#icon-gradient-3)" />
                    <defs>
                      <linearGradient id="icon-gradient-3" x1="2" y1="2" x2="22" y2="22" gradientUnits="userSpaceOnUse">
                        <stop stopColor="#ED0687" />
                        <stop offset="1" stopColor="#FE6C0F" />
                      </linearGradient>
                    </defs>
                  </svg>
                </div>
                <div className="auth-feature-text">
                  <h3>{isGlobalPage ? 'Encrypted' : 'Happy Customers'}</h3>
                  <p>{isGlobalPage ? 'Everything you say is protected end to end.' : 'Join thousands of satisfied users on their cosmic journey'}</p>
                </div>
              </div>
            </div>

            <div className="auth-rating">
              <div className="auth-stars">
                {[...Array(5)].map((_, i) => (
                  <svg key={i} width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M10 0L12.5758 6.90983L20 8.09017L15 13.0902L16.1803 20.5L10 17.0902L3.81966 20.5L5 13.0902L0 8.09017L7.42417 6.90983L10 0Z" fill="#FFB800" />
                  </svg>
                ))}
              </div>
              <span className="auth-rating-text">4.8/5 from 10,000+ users</span>
            </div>
          </div>

          <div className="auth-blob auth-blob-1"></div>
          <div className="auth-blob auth-blob-2"></div>
        </div>

        {/* Right Panel - Auth Form */}
        <div className="auth-form-panel">
          <button className="auth-close-button" onClick={onClose} aria-label="Close">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 6L6 18M6 6L18 18" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>

          {step === 'phone' ? (
            <div className="auth-form-content">
              <h3 className="auth-form-title">Enter your mobile number to Login/Signup</h3>

              {error && <div className="auth-error-message">{error}</div>}

              <form onSubmit={handleSendOTP} className="auth-form">
                <div id="recaptcha-container" ref={recaptchaContainerRef} style={{ display: 'none' }}></div>

                <div className="auth-input-group">
                  <label htmlFor="phone-input" className="auth-label">Mobile number*</label>
                  <div className="auth-phone-input">
                    <div className="auth-country-dropdown" ref={dropdownRef}>
                      <div className="auth-country-code" onClick={() => {
                        // Re-check at open time too — the mq 'change' listener
                        // can be throttled away in backgrounded/hidden tabs.
                        setIsMobileViewport(window.matchMedia('(max-width: 640px)').matches);
                        setIsCountryDropdownOpen(!isCountryDropdownOpen);
                      }}>
                        <span className="auth-country-flag">
                          {countries.find(c => c.iso === selectedIsoCode)?.flag || '🇮🇳'}
                        </span>
                        <span>{countryCode}</span>
                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg"
                          style={{ transform: isCountryDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.3s ease', marginLeft: '4px' }}>
                          <path d="M3 4.5L6 7.5L9 4.5" stroke="rgba(255, 255, 255, 0.6)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </div>
                      {isCountryDropdownOpen && (() => {
                        const pickerContent = (
                          <>
                            <div className="auth-country-search-wrapper">
                              <input
                                type="text"
                                placeholder="Search country..."
                                value={countrySearch}
                                onChange={(e) => setCountrySearch(e.target.value)}
                                className="auth-country-search-input"
                                // No autoFocus on mobile: the keyboard would
                                // immediately cover most of the list.
                                autoFocus={!isMobileViewport}
                              />
                            </div>
                            {countries
                              .filter(c =>
                                c.name.toLowerCase().includes(countrySearch.toLowerCase()) ||
                                c.code.includes(countrySearch)
                              )
                              .map((country) => (
                                <div key={`${country.iso}-${country.code}`} className={`auth-country-option ${selectedIsoCode === country.iso ? 'active' : ''}`}
                                  onClick={() => {
                                    setCountryCode(country.code);
                                    setSelectedIsoCode(country.iso); // [NEW] Update ISO on selection
                                    setIsCountryDropdownOpen(false);
                                    setCountrySearch('');
                                  }}>
                                  <span className="auth-country-flag">{country.flag}</span>
                                  <span className="auth-country-name">{country.name}</span>
                                  <span className="auth-country-code-text">{country.code}</span>
                                </div>
                              ))}
                          </>
                        );
                        // Mobile: portal bottom sheet under document.body. The
                        // modal container has overflow:hidden AND a transform
                        // (+ the overlay a backdrop-filter), so anything
                        // positioned inside it is clipped and even
                        // position:fixed is re-anchored to the container — the
                        // dropdown rendered with its search box invisible and
                        // a 3-row sliver of list. A portal is the only escape.
                        return isMobileViewport
                          ? createPortal(
                              <>
                                <div
                                  className="auth-country-sheet-backdrop"
                                  onClick={() => setIsCountryDropdownOpen(false)}
                                />
                                <div className="auth-country-sheet" ref={countrySheetRef}>
                                  <div className="auth-country-sheet-handle" />
                                  {pickerContent}
                                </div>
                              </>,
                              document.body,
                            )
                          : <div className="auth-country-dropdown-menu">{pickerContent}</div>;
                      })()}
                    </div>
                    <input
                      id="phone-input" type="tel" value={phoneNumber}
                      onChange={(e) => setPhoneNumber(e.target.value.replace(/[^0-9]/g, ''))}
                      placeholder="Mobile number" className="auth-phone-field" required
                    />
                  </div>
                </div>

                {/* HIDDEN CAPTCHA CONTAINER - Still exists for SDK logic */}
                {msg91Auth && (
                  <div
                    id="msg91-captcha-container"
                    ref={msg91CaptchaContainerRef}
                    // Use visibility hidden so it takes 0 space but exists
                    style={{ position: 'absolute', top: '-9999px', visibility: 'hidden' }}
                  ></div>
                )}

                <button
                  type="submit"
                  className="auth-send-otp-button"
                  disabled={isLoading || !phoneNumber}
                >
                  {isLoading ? 'Sending...' : 'Send OTP'}
                </button>

                {/* Google sign-in is offered to NON-Indian users only (phone
                    OTP via MSG91 covers Indian numbers; foreign numbers have
                    OTP friction, so Google is their primary path). Gated on the
                    geoapify-detected location (userCountry), NOT the phone-code
                    picker, so it stays stable regardless of what the user types
                    or selects. */}
                {userCountry && userCountry.toUpperCase() !== "IN" && (
                  <>
                    <div className="auth-divider"><span>or</span></div>

                    <button type="button" className="auth-google-button" onClick={() => { onClose(); onGoogleSignIn(); }}>
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginRight: '8px' }}>
                        <path d="M19.6 10.227c0-.709-.064-1.39-.182-2.045H10v3.868h5.382a4.6 4.6 0 01-1.996 3.018v2.51h3.232c1.891-1.742 2.982-4.305 2.982-7.35z" fill="#4285F4" />
                        <path d="M10 20c2.7 0 4.964-.895 6.618-2.423l-3.232-2.509c-.895.6-2.04.955-3.386.955-2.605 0-4.81-1.76-5.595-4.123H1.064v2.59A9.996 9.996 0 0010 20z" fill="#34A853" />
                        <path d="M4.405 11.9c-.2-.6-.314-1.24-.314-1.9 0-.66.114-1.3.314-1.9V5.51H1.064A9.996 9.996 0 000 10c0 1.614.386 3.14 1.064 4.49l3.34-2.59z" fill="#FBBC05" />
                        <path d="M10 3.977c1.468 0 2.786.505 3.823 1.496l2.868-2.868C14.959.99 12.695 0 10 0 6.09 0 2.71 2.24 1.064 5.51l3.34 2.59C5.19 5.736 7.395 3.977 10 3.977z" fill="#EA4335" />
                      </svg>
                      Continue with Google
                    </button>
                  </>
                )}

                <div className="auth-whatsapp-checkbox">
                  <input
                    type="checkbox"
                    id="whatsapp-updates"
                    checked={agreeWhatsApp}
                    onChange={(e) => setAgreeWhatsApp(e.target.checked)}
                  />
                  <label htmlFor="whatsapp-updates">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginRight: '0.5rem', verticalAlign: 'middle' }}>
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" fill="#25D366" />
                    </svg>
                    Get updates on WhatsApp
                  </label>
                </div>

                <p className="auth-terms">
                  {/* These pointed at /terms and /privacy, neither of which is
                      a route, so both served the homepage. Someone agreeing to
                      the terms could not actually read them. */}
                  By continuing, you agree to our{" "}
                  <a href="/terms-and-conditions">Terms</a> and{" "}
                  <a href="/privacy-policy">Privacy Policy</a>
                </p>
              </form>
            </div>
          ) : (
            <div className="auth-form-content">
              <h3 className="auth-form-title">Verify Your Number</h3>
              <p className="auth-otp-subtitle">
                {/* One string, so Google Translate sees one text node. */}
                {`We've sent a ${otpLength}-digit code to`}<br /><strong>{countryCode} {phoneNumber}</strong>
              </p>

              <button type="button" className="auth-edit-number-button"
                onClick={() => { setStep('phone'); setOtp(Array(otpLength).fill('')); setError(null); }}
                disabled={isLoading}>
                Edit number
              </button>

              {error && <div className="auth-error-message">{error}</div>}

              <form onSubmit={handleVerifyOTP} className="auth-form">
                <div className="auth-otp-container">
                  {otp.map((digit, index) => (
                    <input
                      key={index} id={`otp-${index}`} type="text" inputMode="numeric"
                      /* NOT maxLength={1}: that truncates a pasted code to one
                         character before any handler runs, and it also blocks
                         SMS autofill from delivering the whole code. Length is
                         enforced in fillOtpFrom instead. */
                      value={digit} onChange={(e) => handleOtpChange(index, e.target.value)}
                      onKeyDown={(e) => handleOtpKeyDown(index, e)}
                      onPaste={(e) => handleOtpPaste(index, e)}
                      /* Lets iOS/Android offer the SMS code above the keyboard. */
                      autoComplete="one-time-code"
                      className="auth-otp-input" required
                    />
                  ))}
                </div>

                <button type="submit" className="auth-verify-button" disabled={isLoading || otp.some(d => !d)}>
                  {isLoading ? 'Verifying...' : 'Verify & Continue'}
                </button>

                <button
                  type="button"
                  className="auth-resend-button"
                  onClick={handleResendOTP}
                  disabled={isLoading || resendTimer > 0}
                  style={{ opacity: resendTimer > 0 ? 0.5 : 1, cursor: resendTimer > 0 ? 'not-allowed' : 'pointer' }}
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M14 8C14 11.3137 11.3137 14 8 14C4.68629 14 2 11.3137 2 8C2 4.68629 4.68629 2 8 2V8L12 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  {resendTimer > 0 ? `Resend in ${resendTimer}s` : 'Resend Code'}
                </button>

                <div className="auth-security-note">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M10 1L2 5V9C2 13.55 5.16 17.74 10 19C14.84 17.74 18 13.55 18 9V5L10 1Z" stroke="rgba(255, 255, 255, 0.5)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M8 10L9.5 11.5L12 9" stroke="rgba(255, 255, 255, 0.5)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <div>
                    <p className="auth-security-title">Secure Verification</p>
                    <p className="auth-security-desc">Your information is protected with end-to-end encryption</p>
                  </div>
                </div>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default AuthModal;